import { Command, Option } from "clipanion";
import { DefaultAzureCredential } from "@azure/identity";
import { M8tCommand } from "../../lib/m8t-command.js";
import { colors, renderJson, resolveOutputMode } from "../../lib/output.js";
import { resolveFoundryProject } from "../../lib/foundry-project.js";
import { getAzAccount } from "../../lib/auth.js";
import { listM8tAgents } from "../../lib/foundry-discovery.js";
import { parseBrainLink } from "@m8t-stack/api-contract";

export class BrainListCommand extends M8tCommand {
  static paths = [["brain", "list"]];
  static usage = Command.Usage({ description: "List brain-enabled workers + their repos." });

  subscription = Option.String("--subscription");
  endpoint = Option.String("--endpoint");
  output = Option.String("--output");

  async executeCommand(): Promise<number> {
    // Normalize output: clipanion leaves a descriptor object on the property
    // until argv parsing; when set directly in tests (or not supplied), treat
    // anything that is not a plain string as "pretty" (our diagnostic default).
    const outputFlag =
      this.output === "json" || this.output === "auto" || this.output === "pretty"
        ? (this.output)
        : "pretty";
    const mode = resolveOutputMode(outputFlag, this.context.stdout as NodeJS.WriteStream);

    const credential = new DefaultAzureCredential();

    // getAzAccount() fallback for subscriptionId (same pattern as Tasks 16-18).
    const account = await getAzAccount();
    const subscriptionId =
      (typeof this.subscription === "string" ? this.subscription : undefined) ??
      account.subscriptionId;

    const project = await resolveFoundryProject({
      credential,
      subscriptionId,
      interactive: (this.context.stdout as NodeJS.WriteStream).isTTY === true,
      endpoint: typeof this.endpoint === "string" ? this.endpoint : undefined,
    });

    const all = await listM8tAgents({ credential, projectEndpoint: project.endpoint });
    const brainEnabled = all
      .map((a) => ({ ...a, brain: parseBrainLink(a.metadata) }))
      .filter((a): a is typeof a & { brain: NonNullable<ReturnType<typeof parseBrainLink>> } => a.brain != null);

    if (mode === "json") {
      this.context.stdout.write(renderJson(brainEnabled) + "\n");
      return 0;
    }

    if (brainEnabled.length === 0) {
      this.context.stdout.write(`${colors.dim("No brain-enabled workers.")}\n`);
      return 0;
    }

    for (const a of brainEnabled) {
      const linkMode = a.brain.installationId ? "App" : "PAT";
      this.context.stdout.write(
        `  ${colors.field(a.name)}  ↔  ${colors.field(a.brain.repo)}  ${colors.dim(`(${linkMode}, conn ${a.brain.credentialRef})`)}\n`,
      );
    }
    return 0;
  }
}
